import { Tooltip } from "antd";

import CheckIcon from "@/assets/icons/check.svg?react";
import { Workspace } from "./workspace";
import { WorkspaceItemMenu } from "./WorkspaceItemMenu";
import { formatDateText } from "@/utils/dateUtils";

type WorkspaceItemProps = {
  workspace: Workspace;
  isSelected: boolean;
  isMenuOpen: boolean;
  onSelect: (workspaceId: number) => void;
  onMenuOpenChange: (open: boolean) => void;
  onDuplicate: (workspaceId: number) => void;
  onRename: (
    workspaceId: number,
    newTitle: string,
    isVivoTemplate?: boolean,
  ) => void;
  onDelete: (workspaceId: number) => void;
  existingNames?: string[];
};

function formatSubtitle(workspace: Workspace): string {
  if (workspace.is_vivo_template) {
    return "Provided by Vivo";
  }

  const modifiedDate = new Date(workspace.modified_at);
  const createdDate = new Date(workspace.created_at);

  const isModified = modifiedDate.getTime() !== createdDate.getTime();
  const date = isModified ? modifiedDate : createdDate;
  const prefix = isModified ? "Modified" : "Created";

  return `${prefix} ${formatDateText(date.toISOString())}`;
}

export function WorkspaceItem({
  workspace,
  isSelected,
  isMenuOpen,
  onSelect,
  onMenuOpenChange,
  onDuplicate,
  onRename,
  onDelete,
  existingNames,
}: WorkspaceItemProps) {
  const subtitle = formatSubtitle(workspace);

  return (
    <div
      data-testid="workspace-item"
      data-workspace-id={workspace.id}
      data-workspace-title={workspace.title}
      className={`flex items-center justify-between p-2 rounded-md border border-[#E0E0E0] hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-[#F5FDFF]" : ""} relative`}
      onClick={() => onSelect(workspace.id)}
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-[2px]">
            {isSelected && (
              <Tooltip title="Selected Workspace">
                <span className="shrink-0">
                  <CheckIcon className="text-[#4A9CB8] w-5 h-5" />
                </span>
              </Tooltip>
            )}
            <span
              data-testid="workspace-item-title"
              className={`font-semibold text-[14px] ${isSelected ? "text-[#064C68]" : "text-black"}`}
            >
              {workspace.title}
            </span>
          </div>
          <span
            data-testid={
              workspace.is_vivo_template
                ? "workspace-item-provided-by-vivo"
                : undefined
            }
            className={`text-[#8C8C8C] text-sm ${workspace.is_vivo_template ? "italic" : ""}`}
          >
            {subtitle}
          </span>
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <WorkspaceItemMenu
          workspace={workspace}
          isOpen={isMenuOpen}
          onOpenChange={onMenuOpenChange}
          onDuplicate={onDuplicate}
          onRename={onRename}
          onDelete={onDelete}
          existingNames={existingNames}
        />
      </div>
    </div>
  );
}
